import React from 'react';
import { Loader, ShieldCheck } from 'lucide-react';
import Sheet from '../../../common/Sheet';

/** The statement the clinician signs when submitting a chart (shown on the visit note) */
export const CHART_ATTESTATION_STATEMENT = 'I attest that this documentation is accurate and complete and describes care I provided.';

export interface SubmitChartSheetProps {
  open: boolean;
  onClose: () => void;
  /** Non-invalidated entries in the chart */
  entryCount: number;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
}

/**
 * Phase 18 M3: confirm and sign the chart of the visit just ended. Shows the attestation
 * statement and how many entries are being signed.
 */
const SubmitChartSheet: React.FC<SubmitChartSheetProps> = ({ open, onClose, entryCount, busy, error, onConfirm }) => (
  <Sheet
    open={open}
    onClose={() => !busy && onClose()}
    title="Sign and submit chart"
    subtitle={`${entryCount} chart ${entryCount === 1 ? 'entry' : 'entries'}`}
    footer={(
      <div className="flex gap-2">
        <button type="button" onClick={onClose} disabled={busy} className="min-h-[48px] flex-1 rounded-lg border border-gray-300 text-base font-medium text-gray-700 md:flex-none md:px-5">Keep reviewing</button>
        <button type="button" onClick={onConfirm} disabled={busy} className="flex min-h-[48px] flex-[2] items-center justify-center gap-2 rounded-lg bg-slate-800 text-base font-semibold text-white disabled:opacity-50 md:flex-none md:px-6">
          {busy ? <><Loader size={18} className="animate-spin" />Submitting…</> : 'I attest — submit'}
        </button>
      </div>
    )}
  >
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
        <ShieldCheck size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
        <p data-testid="attestation-statement">{CHART_ATTESTATION_STATEMENT}</p>
      </div>
      <p className="text-sm text-gray-700" data-testid="submit-entry-count">
        You are signing {entryCount} chart {entryCount === 1 ? 'entry' : 'entries'}. After you submit, changes are made as amendments.
      </p>
      {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    </div>
  </Sheet>
);

export default SubmitChartSheet;
